"use client";

import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { captureException } from "@/lib/posthog";

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
      <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
      <p className="text-gray-600 text-center mb-6 max-w-md">
        We&apos;ve been notified of this issue and are working to fix it. Please
        try refreshing the page or contact support if the problem persists.
      </p>
      <div className="flex gap-4">
        <Button onClick={resetError} variant="default" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
        <Button
          onClick={() => {
            if (typeof window !== "undefined") {
              window.location.href = "/";
            }
          }}
          variant="outline"
          size="sm"
        >
          Go Home
        </Button>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Ignore Next.js RSC serialization errors - these are often false positives
    // when client components have state with non-plain objects
    if (
      error.message?.includes("Only plain objects") &&
      error.message?.includes("Client Components from Server Components")
    ) {
      console.warn(
        "[ErrorBoundary] Ignoring RSC serialization warning:",
        error.message
      );
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Ignore RSC serialization errors
    if (
      error.message?.includes("Only plain objects") &&
      error.message?.includes("Client Components from Server Components")
    ) {
      return;
    }

    // Send to PostHog for tracking
    captureException(error, {
      componentStack: errorInfo.componentStack,
      source: "ErrorBoundary",
    });

    // Only log to console in development
    if (process.env.NODE_ENV !== "production") {
      console.error("Error caught by boundary:", error.message);
      if (errorInfo.componentStack) {
        console.error("Component stack:", errorInfo.componentStack);
      }
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || ErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

export default function ErrorBoundary({
  children,
  fallback,
}: ErrorBoundaryProps) {
  return (
    <ErrorBoundaryClass fallback={fallback}>{children}</ErrorBoundaryClass>
  );
}
